import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Field, Input, PageHead, useToast } from '@w7/ui'
import { sessionApi } from '@/api/sessionApi'
import { getUserProfile } from '@/auth/session'

type UserForm = {
  userName: string
  email: string
  password: string
  confirmpassword: string
  status: string
  _id?: string
  [key: string]: unknown
}

export function CreateUserPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const id = params.get('id')
  const [form, setForm] = useState<UserForm>({
    userName: '',
    email: '',
    password: '',
    confirmpassword: '',
    status: '',
  })

  useEffect(() => {
    void getUserProfile().then((u) => {
      if (u?.accessLevel === 'user') toast.error('Access Denied')
    })
  }, [toast])

  useEffect(() => {
    if (!id) return
    void sessionApi
      .getOneUser({ userId: id })
      .then((res) => {
        const data = res.data as UserForm
        if (data) {
          setForm({
            ...data,
            password: data.password ?? '',
            confirmpassword: '',
            status: String(data.status ?? ''),
          })
        }
      })
      .catch((err: { response?: { data?: { message?: string } } }) => {
        toast.error(err?.response?.data?.message ?? 'Failed to load user')
      })
  }, [id, toast])

  const setField = <K extends keyof UserForm>(key: K, value: UserForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
  }

  const createDisabled = !form.status || !form.userName || !form.email || !form.password
  const editDisabled = !form.status || !form.userName || !form.email

  const saveCreate = async (e: FormEvent) => {
    e.preventDefault()
    if (id) return
    if (form.password !== form.confirmpassword) {
      toast.error('Password and Confirm Password should be same!')
      return
    }
    try {
      const res = await sessionApi.saveUserData({ ...form })
      if (res.data && res.status === 200) {
        toast.success('User Save Successfully')
        navigate('/userList')
      } else {
        toast.error('Something Went Wrong')
      }
    } catch {
      toast.error('Something Went Wrong')
    }
  }

  const saveEdit = async () => {
    if (!id) return
    const data = { ...form }
    if (!data.confirmpassword) data.confirmpassword = ''
    if (data.password !== data.confirmpassword) {
      toast.error('Password and Confirm Password should be same!')
      return
    }
    try {
      const res = await sessionApi.updateUser(data)
      if (res.data && res.status === 200) {
        toast.success('User Updated Successfully')
        navigate('/userList')
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Something Went Wrong',
      )
    }
  }

  return (
    <div>
      <PageHead title={id ? 'Edit User' : 'Create User'} onBack={() => navigate('/userList')} />
      <form
        className="sm-card"
        style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 12 }}
        onSubmit={(e) => void saveCreate(e)}
      >
        <Field label="Name" required>
          {({ id: fid, describedBy, invalid }) => (
            <Input
              id={fid}
              aria-describedby={describedBy}
              invalid={invalid}
              placeholder="Display Name"
              value={form.userName}
              onChange={(e) => setField('userName', e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="User Name" required>
          {({ id: fid, describedBy, invalid }) => (
            <Input
              id={fid}
              aria-describedby={describedBy}
              invalid={invalid}
              placeholder="User Name"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="Password">
          {({ id: fid, describedBy, invalid }) => (
            <Input
              id={fid}
              aria-describedby={describedBy}
              invalid={invalid}
              type="password"
              placeholder="Password"
              value={form.password}
              onChange={(e) => setField('password', e.target.value)}
            />
          )}
        </Field>
        <Field label="Confirm Password">
          {({ id: fid, describedBy, invalid }) => (
            <Input
              id={fid}
              aria-describedby={describedBy}
              invalid={invalid}
              type="password"
              placeholder="Confirm Password"
              value={form.confirmpassword}
              onChange={(e) => setField('confirmpassword', e.target.value)}
            />
          )}
        </Field>
        <Field label="Status" required>
          {({ id: fid }) => (
            <select
              id={fid}
              className="w7-input"
              value={form.status}
              onChange={(e) => setField('status', e.target.value)}
              required
            >
              <option value="">Select status</option>
              <option value="Enable">Enable</option>
              <option value="Disable">Disable</option>
            </select>
          )}
        </Field>

        {!id ? (
          <Button type="submit" variant="primary" disabled={createDisabled} style={{ marginTop: 8 }}>
            Create
          </Button>
        ) : (
          <div className="sm-row" style={{ marginTop: 8 }}>
            <Button type="button" onClick={() => navigate('/userList')}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={editDisabled}
              onClick={() => void saveEdit()}
            >
              Edit
            </Button>
          </div>
        )}
      </form>
    </div>
  )
}
